import React, { createContext, useContext, useEffect, useState } from 'react';
import { SystemDefaultColors } from '../constants/systemColors';
import { UserThemeEnum } from '../enums/UserThemeEnum';
import Logo from '../../src/assets/company/logo.png';
import LogoWithName from "../assets/logos/logo.png"
import { ConfigProvider } from 'antd';

const STATIC_COLORS = {
  PRIMARY_BLUE: SystemDefaultColors.PRIMARY_BLUE,
  INPUT_BORDER: SystemDefaultColors.INPUT_BORDER,
};

const VITE_THEME = {
  LIGHT: {
    LOGO: Logo,
    LOGO_WITH_NAME: LogoWithName,
    PRIMARY_COLOR: SystemDefaultColors.PRIMARY_COLOR,
    PRIMARY_FONT_COLOR:
      SystemDefaultColors.PRIMARY_FONT_COLOR,
    SECONDARY_COLOR: SystemDefaultColors.SECONDARY_COLOR,
    PRIMARY_BACKGROUND:
      SystemDefaultColors.PRIMARY_BACKGROUND,
    INPUT_BACKGROUND: SystemDefaultColors.INPUT_BACKGROUND,
    PLACEHOLDER: SystemDefaultColors.PLACEHOLDER,
    ...STATIC_COLORS,
  },
};

interface ThemeContextProps {
  activeThemeConfig: ThemeProperties;
  activeTheme: UserThemeEnum;
}

export interface ThemeProperties {
  LOGO: string;
  LOGO_WITH_NAME: string;
  PRIMARY_COLOR: string;
  PRIMARY_FONT_COLOR: string;
  SECONDARY_COLOR: string;
  PRIMARY_BACKGROUND: string;
  INPUT_BACKGROUND: string;
  INPUT_BORDER: string;
  PLACEHOLDER: string;
}

export const ThemeContext = createContext<ThemeContextProps>({
  activeThemeConfig: VITE_THEME.LIGHT,
  activeTheme: UserThemeEnum.LIGHT,
});

export const ThemeContextProvider = ({ children }: React.PropsWithChildren) => {
  const themes = JSON.parse(JSON.stringify(VITE_THEME));
  const [activeTheme, setActiveTheme] = useState<UserThemeEnum>(UserThemeEnum.LIGHT);
  const [activeThemeConfiguration, setActiveThemeConfiguration] = useState<
    ThemeProperties | undefined
  >(themes[UserThemeEnum.LIGHT]);

  const activeThemeConfig: ThemeProperties = {
    LOGO: activeThemeConfiguration?.LOGO || '',
    LOGO_WITH_NAME: activeThemeConfiguration?.LOGO_WITH_NAME || '',
    PRIMARY_COLOR: activeThemeConfiguration?.PRIMARY_COLOR || SystemDefaultColors.PRIMARY_COLOR,
    PRIMARY_FONT_COLOR:
      activeThemeConfiguration?.PRIMARY_FONT_COLOR || SystemDefaultColors.PRIMARY_FONT_COLOR,
    SECONDARY_COLOR:
      activeThemeConfiguration?.SECONDARY_COLOR || SystemDefaultColors.SECONDARY_COLOR,
    PRIMARY_BACKGROUND:
      activeThemeConfiguration?.PRIMARY_BACKGROUND || SystemDefaultColors.PRIMARY_BACKGROUND,
    INPUT_BACKGROUND:
      activeThemeConfiguration?.INPUT_BACKGROUND || SystemDefaultColors.INPUT_BACKGROUND,
    INPUT_BORDER: activeThemeConfiguration?.INPUT_BORDER || SystemDefaultColors.INPUT_BORDER,
    PLACEHOLDER: activeThemeConfiguration?.PLACEHOLDER || SystemDefaultColors.PLACEHOLDER,
  };

  useEffect(() => {
    changeTheme(UserThemeEnum.LIGHT);
  }, []);

  const changeTheme = (themeKey: UserThemeEnum) => {
    const themes = JSON.parse(JSON.stringify(VITE_THEME));
    const selectedTheme: ThemeProperties = themes?.[themeKey];
    setActiveTheme(themeKey);
    setActiveThemeConfiguration(selectedTheme);

    document.documentElement.style.setProperty(
      '--primary-color',
      selectedTheme.PRIMARY_COLOR || SystemDefaultColors.PRIMARY_COLOR
    );
    document.documentElement.style.setProperty(
      '--secondary-color',
      selectedTheme.SECONDARY_COLOR || SystemDefaultColors.SECONDARY_COLOR
    );
    document.documentElement.style.setProperty(
      '--primary-bg',
      selectedTheme.PRIMARY_BACKGROUND || SystemDefaultColors.PRIMARY_BACKGROUND
    );
  };

  return (
    <ThemeContext.Provider
      value={{
        activeThemeConfig,
        activeTheme: activeTheme,
      }}
    >
      <ConfigProvider
        componentSize={'large'}
        theme={{
          token: {
            fontSize: 16,
            fontFamily:
              'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
            colorTextBase: SystemDefaultColors.PRIMARY_FONT_BLACK,
            colorPrimary: activeThemeConfig.PRIMARY_COLOR,
            borderRadius: 5,
          },
          components: {
            Button: {
              colorPrimary: activeThemeConfig.PRIMARY_COLOR,
            },
            Menu: {
              colorPrimary: activeThemeConfig.PRIMARY_COLOR,
              itemPaddingInline: 10,
            },
            Radio: {
              colorPrimary: activeThemeConfig.PRIMARY_COLOR,
            },
            DatePicker: {
              colorBgContainer: activeThemeConfig.INPUT_BACKGROUND,
            },
            Checkbox: {
              colorFill: activeThemeConfig.PRIMARY_COLOR,
            },
            Select: {
              optionSelectedColor: activeThemeConfig.PRIMARY_COLOR,
              colorBorder: activeThemeConfig.INPUT_BORDER,
              colorBgContainer: activeThemeConfig.INPUT_BACKGROUND,
              colorTextPlaceholder: activeThemeConfig.PLACEHOLDER,
            },
            Tabs: {
              itemActiveColor: activeThemeConfig.PRIMARY_COLOR,
              inkBarColor: activeThemeConfig.PRIMARY_COLOR,
              itemHoverColor: activeThemeConfig.PRIMARY_COLOR,
              itemSelectedColor: activeThemeConfig.PRIMARY_COLOR,
            },
            Input: {
              activeBorderColor: activeThemeConfig.PRIMARY_COLOR,
              hoverBorderColor: activeThemeConfig.PRIMARY_COLOR,
              colorBgBase: activeThemeConfig.PRIMARY_COLOR,
              colorPrimaryHover: activeThemeConfig.PRIMARY_COLOR,
              colorBgContainer: activeThemeConfig.INPUT_BACKGROUND,
              colorBorder: activeThemeConfig.INPUT_BORDER,
              colorTextPlaceholder: activeThemeConfig.PLACEHOLDER,
            },

            Form: {
              colorPrimary: SystemDefaultColors.PRIMARY_FONT_BLACK,
              colorPrimaryText: SystemDefaultColors.PRIMARY_FONT_BLACK,
              colorText: SystemDefaultColors.PRIMARY_FONT_BLACK,
              colorTextLabel: SystemDefaultColors.PRIMARY_FONT_BLACK,
              labelColor: SystemDefaultColors.PRIMARY_FONT,
              itemMarginBottom: 10,
              fontWeightStrong: 500
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

export const useThemeContext = (): ThemeContextProps => {
  return useContext(ThemeContext);
};
